import { useForm } from "react-hook-form";
import { EsuraApiConnectionPropertiesType } from "../../types/SettingsType";
import { useEsuraApiConnectionProperties } from "../../hooks/useEsuraApiConnectionProperties";
import { CheckIcon, EyeOpenIcon, EyeClosedIcon, TrashIcon } from "@radix-ui/react-icons";
import { Flex, Card, Button, IconButton, Spinner, Tooltip } from "@radix-ui/themes";
import TextFieldComponent from "../../components/inputs/Textfield/TextfieldComponent";
import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "react-query";
import toast from "react-hot-toast";
import {
  createEsuraApiConnectionProperties,
  updateEsuraApiConnectionProperties,
  deleteEsuraApiConnectionProperties,
} from "../../services/esuraApiConnectionProperties";
import { ConfirmAlertDialog } from "../../components/common/ConfirmAlertDialog";
import { yupResolver } from "@hookform/resolvers/yup";
import EsuraApiConnectionPropertiesFormValidation from "../Courses/EsuraApiConnectionPropertiesFormValidation";
import { key_EsuraApiConnectionProperties } from "../../utils/constants";

/**
 * Os `defaultValues` do react-hook-form são lidos uma única vez, na primeira
 * renderização — altura em que a query ainda está em curso. O preenchimento do
 * formulário vem sempre do `useEffect` mais abaixo, nunca daqui.
 */
const EMPTY_VALUES: EsuraApiConnectionPropertiesType = {
  url: "",
  username: "",
  password: "",
  baseUrl: "",
};

export const EsuraApiConnectionProperties = () => {
  const { data, isLoading } = useEsuraApiConnectionProperties();
  const queryClient = useQueryClient();
  const [showPassword, setShowPassword] = useState(false);
  const [updateProperties, setUpdateProperties] =
    useState<EsuraApiConnectionPropertiesType | null>(null);
  const [saveProperties, setSaveProperties] =
    useState<EsuraApiConnectionPropertiesType | null>(null);
  const [removeProperties, setRemoveProperties] =
    useState<EsuraApiConnectionPropertiesType | null>(null);

  const mutationCreate = useMutation({
    mutationFn: createEsuraApiConnectionProperties,
    mutationKey: "createEsuraApiConnectionProperties",
  });
  const mutationUpdate = useMutation({
    mutationFn: updateEsuraApiConnectionProperties,
    mutationKey: "updateEsuraApiConnectionProperties",
  });
  const mutationDelete = useMutation({
    mutationFn: deleteEsuraApiConnectionProperties,
    mutationKey: "deleteEsuraApiConnectionProperties",
  });

  const isSubmitting =
    mutationCreate.isLoading || mutationUpdate.isLoading || mutationDelete.isLoading;

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    formState: { errors },
  } = useForm<EsuraApiConnectionPropertiesType>({
    resolver: yupResolver(EsuraApiConnectionPropertiesFormValidation),
    defaultValues: EMPTY_VALUES,
  });

  useEffect(() => {
    if (!data?.id) return;
    // O GET pode não devolver a senha; nesse caso mantém-se a que já está no
    // formulário, senão o campo esvaziava-se logo após uma gravação bem sucedida.
    reset({ ...data, password: data.password || getValues("password") || "" });
  }, [data, reset, getValues]);

  const confirmSave = async () => {
    if (!saveProperties) return;
    try {
      await mutationCreate.mutateAsync(saveProperties);
      // Repõe com os valores submetidos — o `reset()` sem argumentos voltaria
      // aos `defaultValues` vazios e limpava o formulário à vista do utilizador.
      reset(saveProperties);
      toast.success("Configuração guardada com sucesso!");
      // Ressincroniza a cache para o botão passar a "Actualizar" sem refresh.
      await queryClient.invalidateQueries(key_EsuraApiConnectionProperties);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao guardar a configuração");
    } finally {
      setSaveProperties(null);
    }
  };

  const confirmUpdate = async () => {
    if (!updateProperties) return;
    try {
      await mutationUpdate.mutateAsync(updateProperties);
      reset(updateProperties);
      toast.success("Configuração actualizada!");
      await queryClient.invalidateQueries(key_EsuraApiConnectionProperties);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao actualizar a configuração");
    } finally {
      setUpdateProperties(null);
    }
  };

  const confirmRemove = async () => {
    if (!removeProperties?.id) return;
    try {
      await mutationDelete.mutateAsync(removeProperties.id);
      reset(EMPTY_VALUES);
      toast.success("Configuração removida!");
      // `removeQueries` e não `invalidateQueries`: se o GET seguinte falhar (o
      // registo já não existe), o React Query mantinha o `data` anterior em
      // cache e os botões continuariam a mostrar "Actualizar"/"Remover".
      queryClient.removeQueries(key_EsuraApiConnectionProperties);
    } catch (err: any) {
      toast.error(err?.message || "Erro ao remover a configuração");
    } finally {
      setRemoveProperties(null);
    }
  };

  return (
    <>
      {isLoading ? (
        <Flex align="center" justify="center" p="5">
          <Spinner loading />
        </Flex>
      ) : (
        <Flex direction="column" gap="3" mt="4">
          <Card size="3">
            <form
              onSubmit={handleSubmit((values) => {
                if (data?.id) {
                  setUpdateProperties({ ...values, id: data.id });
                } else {
                  setSaveProperties(values);
                }
              })}
              noValidate
            >
              <Flex direction="column" gap="3">
                <TextFieldComponent
                  name="baseUrl"
                  label="Url Pública do esura local"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Url Pública do esura local" }}
                />
                <TextFieldComponent
                  name="url"
                  label="URL do Esura Central"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "URL do Esura Central" }}
                />

                <TextFieldComponent
                  name="username"
                  label="Nome de utilizador"
                  control={control}
                  errors={errors}
                  fieldProps={{ placeholder: "Introduza o nome de utilizador" }}
                />

                <div style={{ position: "relative", width: "100%" }}>
                  <TextFieldComponent
                    name="password"
                    label="Senha"
                    control={control}
                    errors={errors}
                    fieldProps={{
                      type: showPassword ? "text" : "password",
                      placeholder: "Introduza a senha",
                    }}
                  />
                  <Tooltip content={showPassword ? "Ocultar senha" : "Mostrar senha"}>
                    <IconButton
                      type="button"
                      variant="ghost"
                      color="gray"
                      aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
                      onClick={() => setShowPassword((prev) => !prev)}
                      style={{
                        position: "absolute",
                        right: "var(--space-1)",
                        top: "var(--space-5)",
                      }}
                    >
                      {showPassword ? <EyeOpenIcon /> : <EyeClosedIcon />}
                    </IconButton>
                  </Tooltip>
                </div>

                <Flex direction="row" gap="3" style={{ alignSelf: "end" }}>
                  {data?.id && (
                    <Button
                      type="button"
                      variant="soft"
                      color="red"
                      disabled={isSubmitting}
                      onClick={() => setRemoveProperties(data)}
                    >
                      <TrashIcon /> Remover
                    </Button>
                  )}

                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <Spinner loading>
                        <CheckIcon />
                      </Spinner>
                    ) : (
                      <CheckIcon />
                    )}
                    {data?.id ? "Actualizar" : "Guardar"}
                  </Button>
                </Flex>
              </Flex>
            </form>
          </Card>
        </Flex>
      )}

      <ConfirmAlertDialog
        open={!!saveProperties}
        onOpenChange={(open) => !open && setSaveProperties(null)}
        title="Guardar configuração"
        description="Os dados de ligação ao Esura Central serão guardados."
        confirmLabel="Guardar"
        confirmColor="green"
        disabledConfirm={isSubmitting}
        item={saveProperties}
        onConfirm={confirmSave}
        onCancel={() => setSaveProperties(null)}
      />

      <ConfirmAlertDialog
        open={!!updateProperties}
        onOpenChange={(open) => !open && setUpdateProperties(null)}
        title="Actualizar configuração"
        description="Os dados de ligação ao Esura Central existentes serão substituídos."
        confirmLabel="Actualizar"
        confirmColor="blue"
        disabledConfirm={isSubmitting}
        item={updateProperties}
        onConfirm={confirmUpdate}
        onCancel={() => setUpdateProperties(null)}
      />

      <ConfirmAlertDialog
        open={!!removeProperties}
        onOpenChange={(open) => !open && setRemoveProperties(null)}
        title="Remover configuração"
        description="A ligação ao Esura Central deixará de estar configurada. Pretende continuar?"
        confirmLabel="Remover"
        disabledConfirm={isSubmitting}
        item={removeProperties}
        onConfirm={confirmRemove}
        onCancel={() => setRemoveProperties(null)}
      />
    </>
  );
};
